/**
 * AnimalVision — physically-grounded "how animals see" simulation engine.
 * Exports: window.AnimalVision
 *
 * Design philosophy (the etalon difference): most "animal vision" tools online
 * apply one decorative sepia/desaturate filter per animal. This engine instead
 * composes the *real, separable* transforms that differ between species:
 *
 *   1. COLOUR  — dichromat projection in LMS cone space (Machado-Oliveira 2009 /
 *      Brettel-Viénot-Mollon 1997). Mammalian dichromats (dog, cat, horse, cow,
 *      deer, rabbit…) are red-green blind, blue-yellow preserving — the same
 *      well-validated transform used for human deuteranopia. Insects that lack a
 *      red receptor (bee, fly) darken reds → protanope-like. Monochromats
 *      (octopus) → greyscale. This matches dog-vision.com (A. Péter) for the dog.
 *   2. ACUITY  — a real spatial low-pass (Gaussian blur) whose radius is derived
 *      from the animal's measured visual acuity in cycles-per-degree. A cat
 *      (~5 cpd) is much blurrier than a human (~30 cpd); an eagle (~140 cpd)
 *      cannot be made *sharper* than the source (honest limit) so we only add a
 *      mild contrast/colour boost + a note.
 *   3. SCOTOPIC (night) — rod-dominant, tapetum-lucidum animals (cat, dog, deer)
 *      see in near-dark, but desaturated and grainy, with a Purkinje blue shift.
 *      A "night" toggle applies brightness gain + desaturation toward a rod-
 *      weighted luminance + blue shift + photon grain.
 *
 * HONEST LIMITS (cannot be reconstructed from an sRGB photo — surfaced as clearly
 * labelled "approx" toggles, never presented as literal truth):
 *   - UV vision (bee, pigeon, reindeer, mouse): displays emit no UV. The UV toggle
 *     is an *illustrative* false-colour, not a measurement.
 *   - Infrared pit organ (snake): a thermal-style pseudocolour overlay; the pit
 *     organ senses heat, not light — purely illustrative.
 *   - Field of view / polarization: a single camera frame cannot reproduce them.
 *
 * Sources (λmax in nm, acuity in cpd): dog S429/L555 (Neitz, Geist & Jacobs,
 * Vis. Neurosci. 1989); horse S428/L539, cattle S451/L555 (Jacobs et al. 1998);
 * cat dichromat ~S450/L555; deer S455/L537 (Jacobs et al. 1994); bee UV344/B436/
 * G544 (Menzel & Backhaus); eagle acuity 20/4≈140 cpd (Reymond 1985); pigeon
 * tetrachromat + oil droplets; octopus monochromat 480nm + polarization.
 * Simulation method: Brettel/Viénot/Mollon, JOSA A 14:2647 (1997); Machado,
 * Oliveira & Fernandes, IEEE TVCG 2009.
 */
(function () {
'use strict';

/* ───────────────────────── colour-space helpers ─────────────────────────
   Self-contained (no hard dependency on window.CVD load order). LUTs remove
   all Math.pow from the per-pixel hot loop → real-time on live video. */

function srgbToLinearC(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
function linearToSrgbC(c) { c = c < 0 ? 0 : (c > 1 ? 1 : c); return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055; }

// srgb 0-255 → linear float
var LIN = new Float32Array(256);
for (var i = 0; i < 256; i++) LIN[i] = srgbToLinearC(i);
// linear float 0-1 (quantised to 4096) → srgb 0-255 int
var SRGB = new Uint8ClampedArray(4097);
for (var j = 0; j <= 4096; j++) SRGB[j] = Math.round(255 * linearToSrgbC(j / 4096));
function lin2srgb(v) { v = v < 0 ? 0 : (v > 1 ? 1 : v); return SRGB[(v * 4096) | 0]; }

/* ─────────────── Machado-Oliveira-Fernandes 2009 dichromat matrices ───────────────
   Linear-RGB space. Full = severity 1.0, Half = severity 0.5. Identity = 0.
   Copied from the same reference set as public/js/cvd-simulation.js so the
   engine is independently testable (Node) and load-order independent. */
var MACHADO_FULL = {
  deutan: [[0.367322, 0.860646, -0.227968], [0.280085, 0.672501, 0.047413], [-0.011820, 0.042940, 0.968881]],
  protan: [[0.152286, 1.052583, -0.204868], [0.114503, 0.786281, 0.099216], [-0.003882, -0.048116, 1.051998]]
};
var MACHADO_HALF = {
  deutan: [[0.547494, 0.607765, -0.155259], [0.181692, 0.781742, 0.036566], [-0.010410, 0.027275, 0.983136]],
  protan: [[0.458064, 0.679578, -0.137642], [0.092785, 0.846313, 0.060902], [-0.007494, -0.016807, 1.024301]]
};
var IDENTITY = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];

// Interpolate identity(0) → half(0.5) → full(1.0). `sev` is the blend amount.
function machadoMatrix(type, sev) {
  sev = sev < 0 ? 0 : (sev > 1 ? 1 : sev);
  var full = MACHADO_FULL[type], half = MACHADO_HALF[type];
  if (!full || sev === 0) return [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  var m = [[0, 0, 0], [0, 0, 0], [0, 0, 0]], a, b, t, r, c;
  if (sev <= 0.5) { a = IDENTITY; b = half; t = sev / 0.5; }
  else { a = half; b = full; t = (sev - 0.5) / 0.5; }
  for (r = 0; r < 3; r++) for (c = 0; c < 3; c++) m[r][c] = a[r][c] * (1 - t) + b[r][c] * t;
  return m;
}

/* ───────────────────────── the animal dataset ─────────────────────────
   archetype : colour transform — 'deutan' (red-green collapse, blue-yellow kept,
               most mammals), 'protan' (also darkens red — insects with no red
               receptor), 'achroma' (monochromat), 'none' (tri/tetra-chromat:
               colour ≈ or richer-than human; the real differences are acuity/UV).
   acuityCpd : behavioural visual acuity, cycles per degree (human ≈ 30 here).
   scotopic  : night-vision strength 0..1 (rod dominance + tapetum), drives the
               optional "night" transform.
   special   : 'uv' | 'ir' | 'polar' | null  → an explicitly-illustrative toggle. */
var HUMAN_CPD = 30;

var ANIMALS = [
  { key: 'dog',      emoji: '🐕', en: 'Dog',       archetype: 'deutan', acuityCpd: 6.5, scotopic: 0.6, special: null,    cones: 'S 429 · L 555 nm', uv: false, fov: 250, binoc: 60 },
  { key: 'cat',      emoji: '🐈', en: 'Cat',       archetype: 'deutan', acuityCpd: 5.0, scotopic: 0.85, special: null,   cones: 'S 450 · L 555 nm', uv: false, fov: 200, binoc: 140 },
  { key: 'horse',    emoji: '🐎', en: 'Horse',     archetype: 'deutan', acuityCpd: 7.5, scotopic: 0.5, special: null,    cones: 'S 428 · L 539 nm', uv: false, fov: 350, binoc: 65 },
  { key: 'cow',      emoji: '🐄', en: 'Cow',       archetype: 'deutan', acuityCpd: 6.0, scotopic: 0.5, special: null,    cones: 'S 451 · L 555 nm', uv: false, fov: 330, binoc: 50 },
  { key: 'deer',     emoji: '🦌', en: 'Deer',      archetype: 'deutan', acuityCpd: 5.5, scotopic: 0.65, special: null,   cones: 'S 455 · L 537 nm', uv: false, fov: 310, binoc: 60 },
  { key: 'rabbit',   emoji: '🐇', en: 'Rabbit',    archetype: 'deutan', acuityCpd: 3.0, scotopic: 0.55, special: 'uv',   cones: 'S ~400 · M ~520 nm', uv: true, fov: 360, binoc: 30 },
  { key: 'mouse',    emoji: '🐁', en: 'Mouse',     archetype: 'protan', acuityCpd: 1.5, scotopic: 0.9, special: 'uv',    cones: 'UV 360 · M 508 nm', uv: true, fov: 280, binoc: 40 },
  { key: 'eagle',    emoji: '🦅', en: 'Eagle',     archetype: 'none',   acuityCpd: 140, scotopic: 0.0, special: null,    cones: 'trichromat + UV', uv: true, fov: 300, binoc: 50, enhance: 0.7 },
  { key: 'pigeon',   emoji: '🐦', en: 'Pigeon',    archetype: 'none',   acuityCpd: 12,  scotopic: 0.0, special: 'uv',    cones: 'tetrachromat 409·460·515·567', uv: true, fov: 340, binoc: 25, enhance: 0.5 },
  { key: 'bee',      emoji: '🐝', en: 'Bee',       archetype: 'protan', acuityCpd: 0.5, scotopic: 0.0, special: 'uv',    cones: 'UV 344 · B 436 · G 544 nm', uv: true, fov: 280, binoc: 30 },
  { key: 'fly',      emoji: '🪰', en: 'Housefly',  archetype: 'protan', acuityCpd: 0.5, scotopic: 0.0, special: 'uv',    cones: 'UV ~350 · B ~450 · G ~520 nm', uv: true, fov: 360, binoc: 20 },
  { key: 'goldfish', emoji: '🐟', en: 'Goldfish',  archetype: 'none',   acuityCpd: 1.5, scotopic: 0.2, special: 'uv',    cones: 'tetrachromat 367·434·523·641', uv: true, fov: 180, binoc: 20, enhance: 0.45 },
  { key: 'snake',    emoji: '🐍', en: 'Pit viper', archetype: 'deutan', acuityCpd: 2.5, scotopic: 0.3, special: 'ir',    cones: 'dichromat + IR pit organ', uv: false, fov: 240, binoc: 40 },
  { key: 'octopus',  emoji: '🐙', en: 'Octopus',   archetype: 'achroma', acuityCpd: 4.0, scotopic: 0.6, special: 'polar', cones: 'monochromat 480 nm', uv: false, fov: 320, binoc: 0 },
  { key: 'reindeer', emoji: '🦌', en: 'Reindeer',  archetype: 'deutan', acuityCpd: 5.0, scotopic: 0.7, special: 'uv',    cones: 'dichromat + UV 320–350 nm', uv: true, fov: 320, binoc: 60 }
];

var BY_KEY = {};
for (var k = 0; k < ANIMALS.length; k++) BY_KEY[ANIMALS[k].key] = ANIMALS[k];

function get(key) { return BY_KEY[key] || ANIMALS[0]; }

/* ─────────────── build a per-(animal, blend, options) render state ───────────────
   Precompute everything that's constant across pixels. `blend` (0..1) morphs from
   human (0) → full animal (1) for EVERY axis, so blend=0 is a perfect pass-through. */
function buildState(key, blend, opts) {
  opts = opts || {};
  blend = blend < 0 ? 0 : (blend > 1 ? 1 : blend);
  var a = get(key);

  // Colour matrix (linear RGB). For tri/tetrachromats colour ≈ identity (we cannot
  // *add* colours a human screen can't show); their differences live in acuity/UV.
  var mat = (a.archetype === 'deutan' || a.archetype === 'protan')
    ? machadoMatrix(a.archetype, blend)
    : IDENTITY;
  var achroma = (a.archetype === 'achroma') ? blend : 0;        // greyscale amount

  // Acuity → Gaussian sigma at a reference 900px-wide image. Animal resolves
  // (HUMAN_CPD / acuityCpd)× coarser detail than a human. sigma grows with that
  // ratio; eagles (ratio<1) get 0 (cannot sharpen past source). Constant 0.42 +
  // 7px cap: faithful "soft/blurry" (a cat IS short-sighted) without an
  // unrealistic total smear — animals' brains edge-enhance what the optics blur.
  var ratio = HUMAN_CPD / a.acuityCpd;
  var sigmaRef = ratio <= 1 ? 0 : Math.min(7, 0.42 * (ratio - 1));
  sigmaRef *= blend;

  // Scotopic / night
  var night = !!opts.night;
  var scoto = night ? a.scotopic * blend : 0;

  // Special illustrative overlays (only when the matching toggle is on)
  var uvAmt = (opts.uv && a.special === 'uv') ? 0.5 * blend : 0;
  var irAmt = (opts.ir && a.special === 'ir') ? blend : 0;

  // Vividness for tri/tetrachromats whose colour we can't literally *add* (eagle,
  // pigeon, goldfish): their extra cone types + oil droplets give richer, more
  // saturated, higher-contrast perception. Done PER-PIXEL (not a canvas filter) so
  // it also works on Safari/iOS, where ctx.filter is unsupported — otherwise these
  // animals looked identical to human vision there (QA 2026-06-25).
  var enhance = (a.enhance || 0) * blend;

  return {
    animal: a,
    blend: blend,
    mat: mat,
    achroma: achroma,
    sigmaRef: sigmaRef,
    scoto: scoto,
    scotoGrain: night ? a.scotopic * blend : 0,
    uvAmt: uvAmt,
    irAmt: irAmt,
    enhance: enhance
  };
}

// Gaussian sigma in real displayed pixels for the given drawn width.
function sigmaForWidth(state, drawnW) {
  if (!state.sigmaRef) return 0;
  return state.sigmaRef * (drawnW / 900);
}

/* ─────────────── per-pixel pass: colour + scotopic + UV/IR tint ───────────────
   Mutates imageData.data in place. Blur is applied separately at canvas-draw
   time (Gaussian via ctx.filter, with a JS box-blur fallback). */
function processImageData(imageData, state) {
  var d = imageData.data, n = d.length;
  var m = state.mat, achroma = state.achroma;
  var m00 = m[0][0], m01 = m[0][1], m02 = m[0][2];
  var m10 = m[1][0], m11 = m[1][1], m12 = m[1][2];
  var m20 = m[2][0], m21 = m[2][1], m22 = m[2][2];
  var scoto = state.scoto, grain = state.scotoGrain;
  var uvAmt = state.uvAmt, irAmt = state.irAmt, enh = state.enhance || 0;
  var hasColourXform = (m00 !== 1 || m01 !== 0 || m02 !== 0 || m11 !== 1 || m22 !== 1 || achroma > 0);
  var hasScoto = scoto > 0, hasUv = uvAmt > 0, hasIr = irAmt > 0, hasEnh = enh > 0;
  if (!hasColourXform && !hasScoto && !hasUv && !hasIr && !hasEnh) return imageData; // blend≈0 fast path
  var satK = 1 + 0.95 * enh, conK = 1 + 0.45 * enh; // vividness for tri/tetrachromats

  for (var i = 0; i < n; i += 4) {
    var lr = LIN[d[i]], lg = LIN[d[i + 1]], lb = LIN[d[i + 2]];

    // 1) colour transform (linear RGB)
    if (achroma > 0) {
      var gray = 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
      lr = lr + (gray - lr) * achroma;
      lg = lg + (gray - lg) * achroma;
      lb = lb + (gray - lb) * achroma;
    } else if (hasColourXform) {
      var nr = m00 * lr + m01 * lg + m02 * lb;
      var ng = m10 * lr + m11 * lg + m12 * lb;
      var nb = m20 * lr + m21 * lg + m22 * lb;
      lr = nr; lg = ng; lb = nb;
    }

    // 1b) vividness — tri/tetrachromats (eagle/pigeon/goldfish) perceive richer,
    //     more saturated, higher-contrast colour than humans (more cone types +
    //     oil droplets). Per-pixel saturation + contrast about a linear mid-grey
    //     pivot (~sRGB 0.5). Honest stand-in for colour a screen can't reproduce.
    if (hasEnh) {
      var lumE = 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
      lr = lumE + (lr - lumE) * satK;
      lg = lumE + (lg - lumE) * satK;
      lb = lumE + (lb - lumE) * satK;
      lr = (lr - 0.18) * conK + 0.18;
      lg = (lg - 0.18) * conK + 0.18;
      lb = (lb - 0.18) * conK + 0.18;
    }

    // 2) scotopic / night — rod-weighted luminance (Purkinje: blue-green peak),
    //    desaturate toward it, brightness gain (tapetum), blue shift, grain.
    if (hasScoto) {
      var rodLum = 0.15 * lr + 0.55 * lg + 0.30 * lb;
      lr = lr + (rodLum - lr) * scoto;
      lg = lg + (rodLum - lg) * scoto;
      lb = lb + (rodLum - lb) * scoto;
      var gain = 1 + 1.4 * scoto;                 // up to ~2.4× in full night
      lr *= gain; lg *= gain; lb *= gain;
      lb += 0.04 * scoto;                          // Purkinje blue shift
      if (grain > 0) {
        var nz = (Math.random() - 0.5) * 0.10 * grain;
        lr += nz; lg += nz; lb += nz;
      }
    }

    // 3) UV (approx, illustrative): push mid/bright tones toward violet and lift
    //    blue — a stylised stand-in, NOT a UV measurement.
    if (hasUv) {
      var l3 = 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
      var vr = l3 * 0.75, vg = l3 * 0.25, vb = Math.min(1, l3 * 1.15 + 0.08);
      lr = lr + (vr - lr) * uvAmt;
      lg = lg + (vg - lg) * uvAmt;
      lb = lb + (vb - lb) * uvAmt;
    }

    // 4) IR (approx, illustrative thermal): map luminance to an ironbow ramp.
    if (hasIr) {
      var t = 0.2126 * lr + 0.7152 * lg + 0.0722 * lb; // 0..1 "heat"
      var tr = Math.min(1, t * 1.8);
      var tg = Math.max(0, Math.min(1, t * 1.6 - 0.4));
      var tb = Math.max(0, 0.5 - Math.abs(t - 0.25) * 1.6);
      lr = lr + (tr - lr) * irAmt;
      lg = lg + (tg - lg) * irAmt;
      lb = lb + (tb - lb) * irAmt;
    }

    d[i] = lin2srgb(lr); d[i + 1] = lin2srgb(lg); d[i + 2] = lin2srgb(lb);
  }
  return imageData;
}

/* ─────────────── separable box-blur fallback (iOS Safari < 16 has no ctx.filter)
   Approximates a Gaussian with 3 box passes. radius derived from sigma. ─────────────── */
function boxBlur(imageData, radius) {
  radius = radius | 0;
  if (radius < 1) return imageData;
  var w = imageData.width, h = imageData.height, src = imageData.data;
  var tmp = new Uint8ClampedArray(src.length);
  function pass(s, dst, w, h, r, horizontal) {
    var win = 2 * r + 1;
    for (var y = 0; y < h; y++) {
      for (var c = 0; c < 3; c++) {
        var sum = 0, idx, p;
        for (var k = -r; k <= r; k++) {
          p = Math.min(w - 1, Math.max(0, k));
          idx = horizontal ? (y * w + p) * 4 + c : (p * w + y) * 4 + c;
          sum += s[idx];
        }
        for (var x = 0; x < w; x++) {
          idx = horizontal ? (y * w + x) * 4 + c : (x * w + y) * 4 + c;
          dst[idx] = sum / win;
          var add = Math.min(w - 1, x + r + 1), sub = Math.max(0, x - r);
          var ai = horizontal ? (y * w + add) * 4 + c : (add * w + y) * 4 + c;
          var si = horizontal ? (y * w + sub) * 4 + c : (sub * w + y) * 4 + c;
          sum += s[ai] - s[si];
        }
      }
      // copy alpha
      for (var xa = 0; xa < w; xa++) {
        var ia = horizontal ? (y * w + xa) * 4 + 3 : (xa * w + y) * 4 + 3;
        dst[ia] = s[ia];
      }
    }
  }
  // horizontal then vertical (transpose-style indexing handles vertical)
  pass(src, tmp, w, h, radius, true);
  pass(tmp, src, h, w, radius, false); // note swapped w/h for vertical traversal
  return imageData;
}

var _filterSupported = null;
function canvasFilterSupported() {
  if (_filterSupported !== null) return _filterSupported;
  try {
    var c = document.createElement('canvas');
    var ctx = c.getContext('2d');
    _filterSupported = (typeof ctx.filter !== 'undefined');
  } catch (e) { _filterSupported = false; }
  return _filterSupported;
}

window.AnimalVision = {
  ANIMALS: ANIMALS,
  get: get,
  buildState: buildState,
  processImageData: processImageData,
  sigmaForWidth: sigmaForWidth,
  boxBlur: boxBlur,
  canvasFilterSupported: canvasFilterSupported,
  // exposed for tests / debugging
  _machadoMatrix: machadoMatrix
};

})();
